import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DragEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as api from "../api";
import { Chip, Ic } from "../components/ui";
import { BandaDoarCitire, useGeometrieMacro, useRolProiect, useTipProiect } from "../components/rol";
import EtichetaTip from "../components/EtichetaTip";
import { TitluProiect } from "../components/NumeProiect";
import { Pasi } from "./Pasi";
import { paginilePiesei } from "./dosarclient";
import { numar, t as tr, useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";
import { listSolutions, pornesteStructura as pornesteFaza, type SolutieRand } from "../nav/api";
import PlanseSchimbate, { confirmaPeArhitecturaVeche } from "../nav/PlanseSchimbate";
import { caleOferte, caleRand } from "../nav/selectie";
import { EXTENSII } from "../viewer/cost/oferte";
import { etapaJobului } from "../solutions/shared";
import { useRegistruOptional } from "../nav/Registru";
import { PAL, PAL_TEREN } from "../structure/paletaDesen";

const marime = (n: number) =>
  n > 1e6
    ? `${numar(n / 1e6, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`
    : `${numar(Math.round(n / 1024))} KB`;

/** Actele cu care se pornește un dosar, ca exemple în starea goală.
 *
 *  Lista e ALTA în fiecare limbă, nu o traducere a aceleiași: «PUD» și «PUZ»
 *  n-au corespondent american, iar un «detailed urban plan» n-ar fi recunoscut
 *  de nimeni. Cheile numesc rolul actului (planul zonei, actul de proprietate),
 *  iar fiecare catalog pune actul care poartă acel rol la el acasă.
 */
const EXEMPLE_GOL: Cheie[] = [
  "dosar.gol.ex_urbanism",
  "dosar.gol.ex_plan_zona",
  "dosar.gol.ex_plan_lot",
  "dosar.gol.ex_proprietate",
  "dosar.gol.ex_geotehnic",
  "dosar.gol.ex_topografic",
];

/** Desenul stării goale: un teanc de acte, iar foaia din față poartă conturul
 *  lotului și bulina cu bifă.
 *
 *  Un desen, nu icoana de 18 px mărită. Ecranul ăsta e unul dintre primele pe
 *  care le vede cineva după ce își face un proiect, iar o singură foaie de
 *  hârtie nu spune nici că se așteaptă MAI MULTE acte, nici că ele ajung să fie
 *  citite. Foile din spate sunt teancul, planul de pe foaia din față spune că
 *  actele sunt despre un LOT, iar bulina cu bifă e promisiunea scrisă dedesubt
 *  — documentul se clasifică singur — desenată.
 *
 *  Culorile vin din jetoanele temei, nu din literale: desenul e mobilier de
 *  pagină, nu ilustrație, și trebuie să se miște odată cu tema.
 */
function TeancDeActe() {
  return (
    <svg className="dz-desen" width="132" height="108" viewBox="0 0 132 108"
         fill="none" aria-hidden="true">
      <rect x="16" y="20" width="54" height="72" rx="7"
            transform="rotate(-12 43 56)" opacity=".6"
            fill="var(--srf)" stroke="var(--line2)" strokeWidth="1.5" />
      <rect x="62" y="20" width="54" height="72" rx="7"
            transform="rotate(12 89 56)" opacity=".8"
            fill="var(--srf)" stroke="var(--line2)" strokeWidth="1.5" />
      <rect x="38" y="14" width="56" height="78" rx="8"
            fill="var(--srf)" stroke="var(--line2)" strokeWidth="1.5" />
      {/* Antetul actului și rândurile de sub plan: bare, nu text fals — o
          literă desenată la 3 px e zgomot, iar la mărire ar fi vizibil
          neadevărată. */}
      <rect x="48" y="25" width="28" height="3.5" rx="1.75" fill="var(--line2)" />
      <rect x="48" y="32.5" width="16" height="3.5" rx="1.75" fill="var(--line2)" opacity=".7" />
      <path d="M48 48.5 82 43.5 86 66 55 72.5Z" fill="var(--accbg)"
            stroke="var(--acc)" strokeWidth="1.6" strokeLinejoin="round" />
      {[[48, 48.5], [82, 43.5], [86, 66], [55, 72.5]].map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.9"
                fill="var(--srf)" stroke="var(--acc)" strokeWidth="1.2" />
      ))}
      <rect x="48" y="79" width="34" height="3" rx="1.5" fill="var(--line2)" opacity=".6" />
      <rect x="48" y="85.5" width="17" height="3" rx="1.5" fill="var(--line2)" opacity=".45" />
      <circle cx="95" cy="85" r="11" fill="var(--acc)" stroke="var(--srf)" strokeWidth="3" />
      <path d="m90 85 3.4 3.4 6.6-6.8" stroke="#fff" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Cele patru spălături de apartament ale planului din `TeancDePlanse`. */
const APARTAMENTE = ["#dceff6", "#f4e1ea", "#e4f1dc", "#f6efd6"];

/** Locul planșelor de arhitectură, într-un proiect de structură: un teanc de
 *  planșe, iar cea din față poartă un plan de nivel cu patru apartamente în
 *  jurul casei scării — planurile pe care se așază structura. */
function TeancDePlanse() {
  const camere: [number, number, number, number][] = [
    [31, 20, 24, 22], [57, 20, 24, 22], [31, 50, 24, 24], [57, 50, 24, 24]];
  return (
    <svg className="loc-desen" width="112" height="104" viewBox="0 0 112 104" fill="none"
         aria-hidden="true">
      <rect x="10" y="16" width="62" height="76" rx="6" transform="rotate(-9 41 54)" opacity=".7"
            fill="var(--srf)" stroke="var(--line2)" strokeWidth="1.4" />
      <rect x="40" y="16" width="62" height="76" rx="6" transform="rotate(9 71 54)" opacity=".85"
            fill="var(--srf)" stroke="var(--line2)" strokeWidth="1.4" />
      <rect x="24" y="10" width="64" height="84" rx="7" fill="var(--srf)" stroke="var(--line2)"
            strokeWidth="1.5" />
      {camere.map(([x, y, w, h], i) => (
        <rect key={i} x={x} y={y} width={w} height={h} fill={APARTAMENTE[i]} />
      ))}
      <path d="M31 20h50v54H31z M55 20v22 M57 20v22 M31 42h50 M31 50h50 M55 50v24 M57 50v24"
            stroke="var(--ink)" strokeWidth="1.3" />
      <path d="M50 42v8 M62 42v8" stroke="var(--ink)" strokeWidth="1" />
      <rect x="31" y="81" width="18" height="7" rx="2" fill="var(--code-bg)" />
      <text x="40" y="86.6" textAnchor="middle" fontFamily="IBM Plex Mono, monospace"
            fontSize="5.5" fontWeight="500" fill="var(--mut)">DWG</text>
      <rect x="53" y="83" width="22" height="3" rx="1.5" fill="var(--line2)" />
    </svg>
  );
}

/** Locul studiului geotehnic: un foraj cu straturile în culorile terenului din
 *  `paletaDesen.ts` și nivelul apei subterane — ce se citește din el la
 *  fundații. */
function StudiuGeotehnic() {
  const straturi: [string, number, number][] = [
    [PAL_TEREN.umplutura.culoare, 20, 8], [PAL_TEREN.nisip_prafos.culoare, 28, 12],
    [PAL_TEREN.coeziv_mijlocie.culoare, 40, 16], [PAL_TEREN.coeziv_mare.culoare, 56, 16]];
  const randuri: [number, number, number][] = [
    [24, 22, 1], [32, 16, 0.7], [44, 24, 1], [52, 14, 0.7], [62, 20, 1], [70, 12, 0.7]];
  return (
    <svg className="loc-desen" width="112" height="104" viewBox="0 0 112 104" fill="none"
         aria-hidden="true">
      <rect x="40" y="16" width="62" height="76" rx="6" transform="rotate(8 71 54)" opacity=".8"
            fill="var(--srf)" stroke="var(--line2)" strokeWidth="1.4" />
      <rect x="24" y="10" width="64" height="84" rx="7" fill="var(--srf)" stroke="var(--line2)"
            strokeWidth="1.5" />
      {straturi.map(([c, y, h]) => <rect key={y} x="33" y={y} width="16" height={h} fill={c} />)}
      <path d="M35 45h5M42 48h5M35 51h5M42 54h5M35 61h5M42 64h5M35 67h5M42 70h5"
            stroke="rgba(255,255,255,.55)" strokeWidth="1" />
      <rect x="33" y="20" width="16" height="52" stroke={PAL_TEREN.roca.culoare} strokeWidth="1" />
      <path d="M26 47h6M50 47h4" stroke={PAL.apa} strokeWidth="1.2" strokeDasharray="2 1.5" />
      <path d="M27 43.5h5l-2.5 3.2z" fill={PAL.apa} />
      {randuri.map(([y, w, o]) => (
        <rect key={y} x="55" y={y} width={w} height="3" rx="1.5" fill="var(--line2)" opacity={o} />
      ))}
      <rect x="31" y="81" width="18" height="7" rx="2" fill="var(--code-bg)" />
      <text x="40" y="86.6" textAnchor="middle" fontFamily="IBM Plex Mono, monospace"
            fontSize="5.5" fontWeight="500" fill="var(--mut)">PDF</text>
    </svg>
  );
}

/** Felul planșei, pe rândul ei din locul arhitecturii: pe el verifici dintr-o
 *  privire că au venit toate nivelurile și secțiunile. */
const FEL_PLANSA: Record<string, Cheie> = {
  plan_nivel: "dosar.plansa.plan_nivel",
  sectiune: "dosar.plansa.sectiune",
  fatada: "dosar.plansa.fatada",
  plan_situatie: "dosar.plansa.plan_situatie",
  plan_incadrare: "dosar.plansa.plan_incadrare",
  releveu: "dosar.plansa.releveu",
};

type Trasul = {
  onDragOver: (e: DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent) => void;
};

/** Unul din cele două documente fără de care structura nu pornește, ca un loc
 *  de umplut: gol, gol și blocant (celălalt a venit deja, deci doar ăsta mai
 *  lipsește), în lucru, gata. Locul primește fișierele trase peste el, dar nu
 *  le alege piesa de dosar — o face clasificarea, ca oriunde în dosar; tras în
 *  locul greșit, un studiu tot la studiu ajunge. */
function LocDeDosar({ fel, fisiere, altulAre, inLucru, rand, laAlege, potScrie, peste, trasul }: {
  fel: "arh" | "geo";
  fisiere: api.ProjectFile[];
  altulAre: boolean;
  inLucru: (f: api.ProjectFile) => string | null;
  rand: (f: api.ProjectFile, felPlansa: boolean) => JSX.Element;
  laAlege: () => void;
  potScrie: boolean;
  peste: boolean;
  trasul: Trasul;
}) {
  const t = useT();
  const tnr = useTn();
  const arh = fel === "arh";
  const n = fisiere.length;
  const lucru = fisiere.filter((f) => inLucru(f) !== null).length;
  const stare = n === 0 ? (altulAre ? "blocheaza" : "gol") : lucru ? "lucru" : "gata";
  const pastila = {
    gol: <span className="chip c-out">{t("dosar.loc.lipseste")}</span>,
    blocheaza: <Chip tone="amb">{t("dosar.loc.blocheaza")}</Chip>,
    lucru: (
      <Chip tone="mut">
        <span className="pulsa" />{tnr("dosar.loc.in_lucru#n", lucru, { total: n })}
      </Chip>
    ),
    gata: (
      <Chip tone="ok">
        <Ic n="check" s={11} />{arh ? tnr("dosar.loc.planse#n", n) : t("dosar.loc.primit")}
      </Chip>
    ),
  }[stare];
  return (
    <div className={`card loc loc-${stare}${peste && potScrie ? " over" : ""}`}
         {...(potScrie ? trasul : {})}>
      <div className="loc-cap">
        {arh ? <TeancDePlanse /> : <StudiuGeotehnic />}
        <div className="loc-text">
          <span className="lbl">{t("dosar.loc.cerut", { n: arh ? 1 : 2 })}</span>
          <b>{t(arh ? "dosar.loc.arh_titlu" : "dosar.loc.geo_titlu")}</b>
          <span className="loc-desc">{t(arh ? "dosar.loc.arh_desc" : "dosar.loc.geo_desc")}</span>
          <span className="loc-stare">{pastila}</span>
        </div>
      </div>
      {n === 0 ? potScrie && (
        <div className="loc-trage">
          <Ic n="upload" s={12} />{t("dosar.loc.trage")}
          <button type="button" className="loc-alege" onClick={laAlege}>
            {t("dosar.loc.alege")}
          </button>
        </div>
      ) : (
        <div className="loc-fisiere">{fisiere.map((f) => rand(f, arh))}</div>
      )}
      {/* Studiul nu se citește în dosar, ci la fundații: fără nota asta, un
          studiu primit și necitit ar arăta ca unul uitat. */}
      {!arh && stare === "gata" && <p className="note loc-nota">{t("dosar.loc.geo_nota")}</p>}
    </div>
  );
}

/** Pastila de stare a unei rulări — SAU NIMIC, când rularea a reușit.
 *
 * „Documentele au fost procesate · 8 doar clasificate" rămânea pe ecran pentru
 * totdeauna, la fel și „Se importă soluția externă" al unei rulări care își
 * pierduse pulsul. Un mesaj care nu pleacă niciodată nu mai e citit, iar când
 * chiar apare ceva de citit e deja învățat ca zgomot (Stefan, 27.08.2026: «nu
 * vreau să mai apară niciunul din ele»).
 *
 * Regula, de aici înainte: pastila există doar cât timp arată MIȘCARE sau cere
 * ceva de la om.
 *
 *   rulează        → pastilă de progres, cu pasul la care e
 *   a picat        → pastilă roșie, rămâne: e o cerere către om
 *   fără puls      → pastilă roșie, tot o cerere — și e exact modul în care un
 *                    mesaj de progres rămânea agățat pe ecran la infinit
 *   a reușit       → NIMIC. Că treaba e gata se vede din dosar (stările de pe
 *                    rânduri) și din butoanele de ieșire, nu dintr-un afiș.
 *
 * `presupuneri` se arată cât timp rulează sau după ce a picat — ele spun cu ce
 * a plecat rularea („fără studiu geotehnic în dosar"). După ce a reușit, sunt o
 * proprietate a SOLUȚIEI produse și se citesc în Soluții, nu în dosar.
 */
function PastilaJob({ job, titluRulare, titluEsec, acum }: {
  job: api.Job | null;
  titluRulare: string;
  titluEsec: string;
  /** «acum», adus din afară ca să se reevalueze la fiecare tic de polling. */
  acum: number;
}) {
  if (!job) return null;
  const ruleaza = job.status === "running" || job.status === "pending";
  const mort = ruleaza && job.is_dead;
  if (!ruleaza && job.status !== "failed") return null;
  const rau = job.status === "failed" || mort;
  const etapa = ruleaza && !mort ? etapaJobului(job) : null;
  return (
    <div className={`callout ${rau ? "bad" : ""}`}>
      <b>{job.status === "failed" ? titluEsec : titluRulare}</b>
      {etapa && <> · {etapa}</>}
      {/* DE CÂT TIMP. Importul unui dosar de planșe stă șapte minute pe același
          pas («citesc planșele de arhitectură»), iar un text care nu se schimbă
          arată identic cu un mesaj rămas agățat — de aici și întrebarea „de ce
          nu pleacă?". Cifra care crește e singurul lucru care deosebește o
          rulare care lucrează de una uitată pe ecran. */}
      {ruleaza && !mort && <> · <span className="mono">{durata(job, acum)}</span></>}
      {mort && <>{tr("dosar.mort")}</>}
      {job.error?.message && <> · {job.error.message}</>}
      {(job.presupuneri ?? []).map((p, i) => <span key={i}> · {p}</span>)}
    </div>
  );
}

/** Panoul citirii ÎN CURS — în locul pastilei de o linie, cât timp jobul e viu.
 *
 * Pastila spunea „Se procesează documentele · citesc actele · 3 s" și arăta la
 * fel ca o notă rămasă pe ecran: omul nu înțelegea că trebuie să AȘTEPTE și
 * apăsa «Continuă» pe un dosar pe jumătate citit. De aici inelul care se
 * rotește, bara cu partea citită plină și restul în mișcare, și numărul de
 * acte citite din total. Cifrele vin din stările fișierelor (`citire_stare`):
 * jobul nu scrie «făcut/total» pe rândul lui (v. `importer/i18n/etape.din`).
 *
 * Când jobul a picat sau n-are puls, rămâne `PastilaJob` — e o cerere către
 * om, nu un progres.
 */
function PanouCitire({ job, acum, citite, total }: {
  job: api.Job;
  acum: number;
  citite: number;
  total: number;
}) {
  const etapa = etapaJobului(job);
  const procent = total > 0 ? Math.round((citite / total) * 100) : 0;
  return (
    <section className="callout panou-citire" aria-live="polite">
      <div className="panou-citire-cap">
        <span className="inel" aria-hidden="true" />
        <div className="panou-citire-text">
          <div>
            <b>{tr("dosar.citire_ruleaza")}</b>
            {total > 0 && <span className="mut"> · {tr("dosar.citire_din", { citite, total })}</span>}
          </div>
          <div className="mut">
            {etapa && <>{etapa} · </>}
            <span className="mono">{durata(job, acum)}</span>
            {(job.presupuneri ?? []).map((p, i) => <span key={i}> · {p}</span>)}
          </div>
        </div>
      </div>
      <div className="panou-citire-bara" role="progressbar" aria-valuemin={0}
           aria-valuemax={total} aria-valuenow={citite}>
        <span className="plin" style={{ width: `${procent}%` }} />
        <span className="viu" style={{ left: `${procent}%` }} />
      </div>
    </section>
  );
}

/** De cât timp rulează, în cuvinte scurte („12 s", „7 min"). */
function durata(job: api.Job, acum: number) {
  const de_la = Date.parse(job.started_at || job.created_at || "");
  if (!de_la) return "";
  const sec = Math.max(0, Math.round((acum - de_la) / 1000));
  return sec < 90 ? tr("dosar.secunde", { n: sec })
                  : tr("dosar.minute", { n: Math.round(sec / 60) });
}

// Cât de des se întreabă serverul cât timp rulează ceva. Când nu rulează nimic,
// nu se întreabă deloc — un polling care merge în gol pe un tab uitat deschis e
// trafic pe care nu-l cere nimeni.
// De unde vine piesa de dosar. Cinci propoziții fixe, în locul motivului liber
// pe care îl scria clasificatorul: acela rămânea în urmă la prima mutare cu
// mâna („numele conține 'memoriu'" pe un fișier pe care omul tocmai îl mutase
// la studiu geotehnic), fiindcă era scris o dată și nimeni nu-l mai rescria.
// Sursa se scrie în ACEEAȘI cerere cu categoria, deci nu are cum să mintă.
const DE_UNDE: Record<string, Cheie> = {
  regula: "dosar.de_unde.regula",
  titlu: "dosar.de_unde.titlu",
  continut: "dosar.de_unde.continut",
  manual: "dosar.de_unde.manual",
  oferte: "dosar.de_unde.oferte",
};

const POLL_MS = 3000;

/** Ce se poate citi ca ofertă (`viewer/cost/oferte.ts::EXTENSII`): o planșă
 *  de la «Rețete & prețuri» rămâne doar document. */
const citibilCaOferta = (nume: string) =>
  EXTENSII.includes(nume.slice(nume.lastIndexOf(".")).toLowerCase());

/** Ce a ieșit din document pe Planificare → Oferte, într-o propoziție. */
function textOferta(o: api.ProjectFile["oferta"]): string {
  if (!o) return tr("dosar.oferta_necitita");
  if (o.stare === "se_citeste" || o.stare === "citita") return tr("dosar.oferta_se_citeste");
  if (o.stare === "eroare") return tr("dosar.oferta_eroare");
  return o.scenariu ? tr("dosar.oferta_aplicata", { scenariu: o.scenariu }) : tr("dosar.oferta_aplicata_fara");
}

export default function Dossier() {
  const { projectId = "" } = useParams();
  const t = useT();
  const tnr = useTn();
  const [files, setFiles] = useState<api.ProjectFile[]>([]);
  const [categories, setCategories] = useState<api.Category[]>([]);
  const [job, setJob] = useState<api.Job | null>(null);
  // the external-architecture import job (DWG solution -> canonical document)
  const [importJob, setImportJob] = useState<api.Job | null>(null);
  // Are proiectul deja o arhitectură? De asta atârnă ce PROMITE butonul de
  // citire: importul planșelor pleacă odată cu ea numai prima dată (o
  // reprocesare n-are voie să șteargă arhitectura și structura de pe ea).
  const [hasArchitecture, setHasArchitecture] = useState(false);
  const [solutii, setSolutii] = useState<SolutieRand[]>([]);
  const [pornesteStructura, setPornesteStructura] = useState(false);
  const [selected, setSelected] = useState<api.ProjectFile | null>(null);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  // A răspuns prima încărcare a listei? Numai de la ea încoace „zero fișiere"
  // înseamnă un dosar gol; până atunci înseamnă „încă nu știu".
  const [incarcat, setIncarcat] = useState(false);
  // ÎNCĂRCAREA ÎN CURS: numele alese de om, plus câți octeți au plecat din
  // total. Rândurile lor apar stinse în listă înainte să existe în bază — un
  // ecran care nu-și schimbă nimic după un drop de 200 MB arată exact ca unul
  // în care drop-ul n-a fost primit.
  //
  // Progresul e pe TRANȘĂ, nu pe fișier, fiindcă atât știe browserul: corpul
  // multipart pleacă într-un singur flux. Un procent inventat per fișier ar fi
  // arătat mai bine și ar fi fost o minciună.
  //
  // O LISTĂ de tranșe, nu una singură: butonul se blochează cât urcă ceva,
  // dar drop-ul nu, iar omul trage al doilea lot peste primul. Pe 01.10.2026
  // studiul geotehnic tras peste opt DWG-uri a înlocuit tranșa lor, iar
  // terminându-se primul a golit-o de tot — planșele dispăruseră din ecran cât
  // încă urcau. Fiecare tranșă se scoate acum doar pe ea însăși.
  const [incarcari, setIncarcari] = useState<
    { id: number; nume: string[]; octeti: number; trimis: number }[]>([]);
  const busy = incarcari.length > 0;
  // Parsarea planșelor, pe id de fișier (v. `dosar/pregatire.py`). Se cere cât
  // timp vreuna e în lucru; ce e „gata" se deschide instantaneu în viewer.
  const [desene, setDesene] = useState<api.DrawingPrep>({});
  // Actele pe care s-a apăsat «reprocesează» și cărora nu le-a răspuns încă
  // serverul. Doar cât durează apelul: după el, jobul de citire preia semnalul
  // și rândul spune „se citește" ca orice alt act.
  const [reprocesate, setReprocesate] = useState<Set<string>>(new Set());
  // Ceasul pastilelor de rulare: se mișcă odată cu polling-ul, deci «7 min» nu
  // rămâne «6 min» până la următoarea reîncărcare a listei.
  const [acum, setAcum] = useState(() => Date.now());
  const nav = useNavigate();
  // registrul platformei: recitit după o pornire, ca ecranul pe care se merge
  // să găsească rândul nou (sau rularea fără rând) — nu pe cel de acum 30 s
  const registrul = useRegistruOptional();
  const input = useRef<HTMLInputElement>(null);
  // Numărătorul tranșelor de urcare, ca fiecare să-și știe rândul în listă.
  const transe = useRef(0);
  // Fără drept de scriere ecranul rămâne o listă de citit: nici încărcare,
  // nici ștergere, nici reclasificare, nici pornirea citirii actelor.
  const { potScrie } = useRolProiect();
  // Tipul proiectului hotărăște ce cere dosarul: actele terenului, cu ieșirea
  // spre Detalii teren, sau planșele și studiul unei arhitecturi existente,
  // cu pornirea structurii. Cât nu se știe, sub cap nu se desenează nimic.
  const tip = useTipProiect();
  const structura = tip === "structura";
  // Alegerea de la crearea proiectului: Soluția 1 care vine singură după
  // import e generată sau importată din planșe; butonul de aici o urmează.
  const importa = useGeometrieMacro() === "importata";

  const reload = useCallback(async () => {
    const [f, j, s] = await Promise.all([
      api.listFiles(projectId),
      api.listJobs(projectId),
      listSolutions(projectId).then((r) => r.solutii).catch(() => [] as SolutieRand[]),
    ]);
    setFiles(f);
    setJob(j.find((x) => x.kind === "extraction") ?? null);
    setImportJob(j.find((x) => x.kind === "external_architecture") ?? null);
    setHasArchitecture(s.some((x) => x.discipline === "architecture"));
    setSolutii(s);
    setIncarcat(true);
  }, [projectId]);

  useEffect(() => {
    api.listCategories(projectId).then(setCategories).catch(() => undefined);
    reload().catch(() => setError(t("dosar.esec_incarcare")));
  }, [projectId, reload]);

  // La deschiderea dosarului se cere pregătirea planșelor care n-o au. Pentru
  // ce s-a urcat de acum înainte pleacă singură, la urcare; apelul de aici e
  // pentru dosarele de DINAINTE de mecanism, iar pe unul deja pregătit e o
  // citire și nimic altceva. Fără drept de scriere nu se cere: e o pornire de
  // muncă pe server, deci cere rolul de editor.
  useEffect(() => {
    if (!potScrie) {
      api.listDrawingPrep(projectId).then(setDesene).catch(() => undefined);
      return;
    }
    api.prepareDrawings(projectId).then(setDesene).catch(() => undefined);
  }, [projectId, potScrie]);

  // Cât timp vreo planșă se pregătește, starea se cere periodic — pe același
  // ritm ca joburile, și tot numai cât e ceva de urmărit.
  const desenePeDrum = Object.values(desene)
    .some((d) => d.stare === "asteapta" || d.stare === "lucreaza");
  useEffect(() => {
    if (!desenePeDrum) return;
    const timer = setInterval(() => {
      api.listDrawingPrep(projectId).then(setDesene).catch(() => undefined);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [desenePeDrum, projectId]);

  // Cât timp citirea rulează, starea se cere periodic. Se oprește singură când
  // jobul s-a terminat — sau când l-a declarat mort lipsa semnalului de viață.
  // La fel cât se citește o ofertă: scenariul ei îl face privirea dosarului
  // (`control-plane/api/oferte.py::la_zi`), iar rândul spune când e gata.
  const running = job?.status === "running" || job?.status === "pending";
  // Câte acte au o stare de citire, din câte sunt: un act fără stare, cât
  // timp citirea rulează, e un act care încă se citește (v. `inLucru`).
  const acteCitite = files.filter((f) => f.citire_stare).length;
  const importing = importJob?.status === "running" || importJob?.status === "pending";
  const oferteInLucru = files.some((f) => f.oferta?.stare === "se_citeste" || f.oferta?.stare === "citita");
  useEffect(() => {
    if (!running && !importing && !oferteInLucru) return;
    setAcum(Date.now());
    const timer = setInterval(() => {
      setAcum(Date.now());
      reload().catch(() => undefined);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [running, importing, oferteInLucru, reload]);

  const labels = useMemo(() => {
    const map = new Map(categories.map((c) => [c.cheie, c.eticheta]));
    return (key: string | null) =>
      map.get(key ?? "altele") ?? key ?? t("dosar.altele");
  }, [categories, t]);

  // Un fișier cu MAI MULTE acte în el (`dosar === "mixt"`) nu are un card al
  // lui: apare în cardul fiecărei piese, cu paginile ei pe rând (v.
  // `piesaIn`). Piesele «altele» nu se arată — pe un asemenea fișier ele sunt
  // oferta și pozele, nu un act de pus undeva.
  const groups = useMemo(() => {
    const map = new Map<string, api.ProjectFile[]>();
    for (const file of files) {
      const piese = file.dosar === "mixt" && file.piese?.length
        ? [...new Set(file.piese.map((p) => p.dosar).filter((d) => d !== "altele"))]
        : [file.dosar ?? "altele"];
      for (const key of piese) map.set(key, [...(map.get(key) ?? []), file]);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [files]);

  /** Piesele unui fișier mixt care țin de cardul `cheie` — pentru pastila cu
   *  paginile de pe rând; gol pe fișierul obișnuit. */
  const piesaIn = (file: api.ProjectFile, cheie: string) =>
    (file.dosar === "mixt" ? (file.piese ?? []).filter((p) => p.dosar === cheie) : []);

  // Dosarul chiar gol: nimic în listă, nimic pe drum, și lista a venit deja.
  const golPustiu = incarcat && files.length === 0 && !busy;

  // Primirea fișierelor trase cu mâna. Aceleași trei mânere pe amândouă
  // înfățișările zonei de drop — scrise o dată, ca banda și starea goală să nu
  // poată primi drop-ul altfel.
  const trasul = {
    onDragOver: (e: DragEvent) => { e.preventDefault(); setDragging(true); },
    onDragLeave: () => setDragging(false),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      setDragging(false);
      upload(e.dataTransfer.files);
    },
  };

  async function upload(chosen: FileList | null) {
    if (!chosen?.length) return;
    const alese = [...chosen];
    const id = ++transe.current;
    setError("");
    setIncarcari((cur) => [...cur, {
      id,
      nume: alese.map((f) => f.name),
      octeti: alese.reduce((s, f) => s + f.size, 0),
      trimis: 0,
    }]);
    try {
      const result = await api.uploadFiles(projectId, alese, (trimis, total) =>
        setIncarcari((cur) => cur.map((x) => x.id === id
          ? { ...x, trimis, octeti: total || x.octeti } : x)));
      if (result.rejected.length) {
        setError(t("dosar.neacceptate",
                   { fisiere: result.rejected.map((r) => r.name).join(", ") }));
      }
      // Actele urcate se citesc singure, numai ele, și datele consolidate se
      // refac — v. `api/dossier.py: _reia_citirea`.
      //
      // Aici stătea o notă: «citirea în curs nu cuprinde actele tocmai urcate —
      // reprocesează documentele». Era un sfat dat omului pentru o treabă a
      // platformei, și rămânea pe ecran până la următoarea încărcare. Citirea
      // își face acum singură o trecere în plus pentru actele apărute cât rula
      // (v. `importer/app.py: TRECERI_PENTRU_ACTE_NOI`), deci nu mai e nimic de
      // sfătuit — și nimic de afișat.
      // Parsarea planșelor urcate a plecat deja pe server; starea vine în
      // răspuns, ca rândurile să nu aștepte până la primul poll ca să spună
      // că se pregătesc.
      if (result.desene) setDesene((cur) => ({ ...cur, ...result.desene }));
      if (result.citire?.job) setJob(result.citire.job);
      await reload();
    } catch {
      setError(t("dosar.esec_urcare"));
      // Serverul poate să fi păstrat o parte: rândurile se scriu înainte de
      // clasificare, deci lista se cere din nou, ca ecranul să arate ce e.
      reload().catch(() => undefined);
    } finally {
      setIncarcari((cur) => cur.filter((x) => x.id !== id));
      if (input.current) input.current.value = "";
    }
  }

  /** Ce se întâmplă ACUM cu fișierul ăsta — sau null, dacă nu se întâmplă
   *  nimic. Două munci diferite, pe același rând: citirea actului (modelul) și
   *  parsarea planșei (dwg2dxf). A doua e cea care făcea vizualizatorul să se
   *  deschidă în treizeci de secunde, iar acum se plătește la urcare — dar cât
   *  timp se plătește, rândul trebuie s-o spună. */
  function inLucru(file: api.ProjectFile): string | null {
    // Un act fără stare de citire, într-un dosar care se citește ACUM, e un act
    // care se citește: `doar_noi` sare peste cele care au deja extras, iar alea
    // au toate o stare.
    if (reprocesate.has(file.id)) return t("dosar.se_reproceseaza");
    if (running && !file.citire_stare) return t("dosar.se_citeste");
    if (file.oferta?.stare === "se_citeste" || file.oferta?.stare === "citita") return t("dosar.se_citeste_oferta");
    const d = desene[file.id]?.stare;
    if (d === "lucreaza") return t("dosar.desen_se_pregateste");
    if (d === "asteapta") return t("dosar.desen_la_rand");
    return null;
  }

  // Are dosarul planșe de soluție arhitecturală? De asta atârnă ce PROMITE
  // butonul de citire — el duce cu el importul planșelor, prima dată.
  //
  // Butonul «Importă soluția externă» a plecat de aici (Stefan, 27.08.2026).
  async function remove(file: api.ProjectFile) {
    // Dacă dosarul a fost deja citit, ce s-a înțeles din fișierul ăsta rămâne
    // în datele proiectului până la o recitire. Ștergerea fișierului nu retrage
    // valorile ieșite din el, iar un ecran care lasă impresia că da e un ecran
    // în care POT-ul vine dintr-un act care nu mai există.
    const citit = job?.status === "succeeded";
    // …iar planșa din care s-a importat arhitectura o lasă pe aceasta pe o
    // foaie care nu mai e nicăieri: arhitectura nu se schimbă odată cu dosarul
    const aArhitecturii = arhGata?.planse?.citite.find((p) => p.fisier_id === file.id);
    // …iar oferta citită din el pleacă odată cu documentul (e același fișier)
    const intrebare = aArhitecturii
      ? t("dosar.confirma_stergere_plansa_importata",
          { fisier: file.name, niveluri: aArhitecturii.niveluri.join(", ") })
      : file.oferta ? t("dosar.confirma_stergere_oferta", { fisier: file.name })
      : t(citit ? "dosar.confirma_stergere_citit" : "dosar.confirma_stergere",
          { fisier: file.name });
    if (!confirm(intrebare)) return;
    await api.deleteFile(projectId, file.id);
    setSelected(null);
    await reload();
  }

  /** Reprocesează UN act. Cazul pentru care există: zona de interes s-a
   *  schimbat pe o planșă, deci ce s-a citit PRIN ea (conturul lotului,
   *  categoria dedusă din desen) nu mai e valabil (Stefan, 27.08.2026).
   *
   *  Fără confirmare: e o acțiune care ADAUGĂ muncă, nu una care aruncă ceva —
   *  singurul cost al unei apăsări greșite e o citire în plus pe un act. */
  async function reprocess(file: api.ProjectFile) {
    setError("");
    setReprocesate((cur) => new Set(cur).add(file.id));
    try {
      const r = await api.reprocessFile(projectId, file.id);
      if (r.desene) setDesene((cur) => ({ ...cur, ...r.desene }));
      if (r.citire?.job) setJob(r.citire.job);
      await reload();
    } catch {
      setError(t("dosar.esec_reprocesare", { fisier: file.name }));
    } finally {
      setReprocesate((cur) => {
        const next = new Set(cur);
        next.delete(file.id);
        return next;
      });
    }
  }

  // «GENEREAZĂ STRUCTURA» — ieșirea din dosarul unui PROIECT DE STRUCTURĂ:
  // planșele arhitecturii existente și studiul geotehnic sunt tot ce-i trebuie
  // structurii, deci proiectul n-are teren, temă sau plan de situație. Până nu
  // sunt amândouă în dosar butonul stă stins și spune ce lipsește.
  const areArhDwg = files.some((f) => f.dosar === "solutie_arhitecturala");
  const areGeoteh = files.some((f) => f.dosar === "studiu_geotehnic");
  const arhGata = useMemo(() => {
    const gata = solutii.filter((s) => s.discipline === "architecture"
                                       && s.status === "ready");
    return gata.find((s) => s.promoted) ?? gata[gata.length - 1] ?? null;
  }, [solutii]);
  const areStructura = !!arhGata && solutii.some((s) =>
    s.discipline === "structure" && s.lineage?.architecture === arhGata.id
    && s.status !== "failed" && s.status !== "abandoned");

  /** Trei cazuri, după cât a ajuns proiectul:
   *   - arhitectura are deja o structură → doar se deschide;
   *   - arhitectura e importată, fără structură → pornește căutarea pe ea;
   *   - nicio arhitectură → pornește importul planșelor, cu marca
   *     `cautare_structura`: căutarea vine singură după el (v.
   *     `control-plane/services/inlantuire.py`, veriga a treia). */
  async function genereazaStructura() {
    setError("");
    setPornesteStructura(true);
    try {
      if (!areStructura) {
        if (arhGata) {
          // pe o arhitectură ieșită din planșe care s-au schimbat de atunci,
          // nu tăcut: omul alege între ea și reimport
          if (!confirmaPeArhitecturaVeche(arhGata)) return;
          // «Generează soluția» pe arhitectura gata (contract §5.1): se merge
          // pe rândul din răspuns — schița, cât Soluția 1 vine după ea
          const r = await pornesteFaza(projectId, {
            kind: "structure", faza: "macro", upstream_solution_id: arhGata.id,
            ...(importa ? { import_structura: true } : {}) });
          await registrul?.reciteste();
          nav(caleRand(projectId, r.solutie));
          return;
        }
        await api.startJob(projectId, "external_architecture",
                           { cautare_structura: true });
        await registrul?.reciteste();
        // Pe arhitectură cât timp importul macină: acolo stă semnul de
        // așteptare, iar dacă importul pică, tot acolo se spune.
        nav(`/p/${projectId}/arhitectura`);
        return;
      }
      nav(`/p/${projectId}/structura?pe=${arhGata?.id ?? ""}`);
    } catch (err) {
      setError(err instanceof api.ApiError && err.message
        ? err.message : t("dosar.structura_esec"));
      await reload().catch(() => undefined);
    } finally {
      setPornesteStructura(false);
    }
  }

  async function recategorise(file: api.ProjectFile, dosar: string) {
    // mutat la «Rețete & prețuri», răspunsul aduce și citirea lui ca ofertă
    const updated = await api.patchFile(projectId, file.id, { dosar });
    setFiles((current) => current.map((f) => (f.id === updated.id ? updated : f)));
    setSelected(updated);
  }

  /** Rândul unui fișier, în cărțile dosarului și în locurile proiectului de
   *  structură. `felPlansa` adaugă felul planșei (plan de nivel, secțiune) —
   *  în locul arhitecturii, pe el se vede că au venit toate nivelurile. */
  function randFisier(file: api.ProjectFile, felPlansa = false, cardul?: string) {
    const plansa = felPlansa && file.categorie ? FEL_PLANSA[file.categorie] : undefined;
    const piese = cardul ? piesaIn(file, cardul) : [];
    return (
      <div key={`${file.id}${cardul ? `@${cardul}` : ""}`}
           className={`rowline ${selected?.id === file.id ? "on" : ""}`}
           onClick={() => setSelected(file)}>
        <span className="fname">{file.name}</span>
        {plansa && <span className="rowline-fel">{t(plansa)}</span>}
        {/* Fișierul cu mai multe acte: pe rândul din cardul piesei stau
            PAGINILE ei, ca omul să vadă că certificatul e p. 8–10 din
            prezentarea agenției, nu tot fișierul. */}
        {piese.map((p) => (
          <span key={p.pagini.join("-")} className="rowline-fel">{paginilePiesei(p.pagini)}</span>
        ))}
        {/* PE RÂND STĂ NUMAI CE E ÎN NEREGULĂ sau în mișcare.
            Aveam trei etichete pe fiecare rând — «propus automat» /
            «confirmat» pentru clasificare și «doar clasificat» pentru
            citire — adică un ecran în care șaptesprezece rânduri
            purtau fiecare o pastilă, deci nicio pastilă nu mai
            însemna nimic (Stefan, 27.08.2026). Toate trei descriau
            starea NORMALĂ: actul e clasificat, iar clasificarea a
            mers. Cine vrea să știe de unde vine eticheta o are în
            panoul din dreapta, cu motivul întreg.

            Ce a rămas pe rând: munca în curs și eșecurile — singurele
            două lucruri care cer ceva de la om. */}
        {inLucru(file) ? (
          <Chip tone="mut"><span className="pulsa" />{inLucru(file)}</Chip>
        ) : (
        <>
        {file.citire_stare === "eroare" && (
          <Chip tone="bad">{t("dosar.necitit")}</Chip>
        )}
        {/* Planșa care n-a putut fi parsată o spune AICI, nu la primul
            clic pe «deschide în viewer»: acolo ar fi un ecran gol cu
            o eroare, după o așteptare degeaba. */}
        {desene[file.id]?.stare === "eroare" && (
          <Chip tone="bad">{t("dosar.desen_necitit")}</Chip>
        )}
        {/* Planșa DTAC e un capsator de referințe externe: fără
            folderul „Xrefed …" urcat, ea se deschide instantaneu și
            se vede aproape goală. Un desen gol care nu spune de ce e
            cea mai proastă combinație. */}
        {desene[file.id]?.stare === "gata"
          && desene[file.id]?.motiv && (
          <Chip tone="amb">{t("dosar.referinte_lipsa")}</Chip>
        )}
        </>
        )}
        {/* `stopPropagation`: fără el, clicul pe coș ar selecta ÎNTÂI
            fișierul, iar dacă omul se răzgândește la confirmare
            rămâne cu inspectorul deschis peste un fișier pe care
            tocmai a hotărât să nu-l atingă. Aceeași grijă la
            reprocesare, din alt motiv: acolo nu e nicio confirmare,
            deci un clic ar face DOUĂ lucruri deodată.

            Amândouă apar la hover, ca până acum: e un caz rar, iar
            șaptesprezece perechi de icoane deodată sunt un ecran care
            țipă. */}
        {potScrie && (
        <>
        <button className="rowline-sterg"
                disabled={reprocesate.has(file.id)}
                title={t("dosar.reproceseaza_titlu",
                         { fisier: file.name })}
                aria-label={t("dosar.reproceseaza_aria",
                              { fisier: file.name })}
                onClick={(e) => { e.stopPropagation(); reprocess(file); }}>
          <Ic n="redo" s={13} />
        </button>
        <button className="rowline-sterg"
                title={t("dosar.sterge_aria", { fisier: file.name })}
                aria-label={t("dosar.sterge_aria", { fisier: file.name })}
                onClick={(e) => { e.stopPropagation(); remove(file); }}>
          <Ic n="x" s={13} />
        </button>
        </>
        )}
      </div>
    );
  }

  // Cărțile de sub locuri: la proiectul de structură, planșele și studiul au
  // locurile lor, deci aici rămân doar celelalte acte.
  const grupuriVizibile = structura
    ? groups.filter(([key]) => key !== "solutie_arhitecturala" && key !== "studiu_geotehnic")
    : groups;

  // Pornirea structurii se aprinde cu planșele ȘI studiul în dosar. De ce stă
  // stinsă se scrie SUB ea, nu doar în `title`: un buton stins care nu spune
  // de ce arată ca unul stricat.
  const structuraPoate = areArhDwg && areGeoteh && !importing && potScrie;
  const motivStructura = importing ? t("dosar.structura_import_ruleaza")
    : !areArhDwg && !areGeoteh ? t("dosar.structura_motiv_gol")
    : !areGeoteh ? t("dosar.structura_motiv_fara_geo")
    : !areArhDwg ? t("dosar.structura_motiv_fara_arh")
    : areStructura ? null
    : arhGata ? t(importa ? "dosar.structura_motiv_pe_arh_import" : "dosar.structura_motiv_pe_arh")
    : t(importa ? "dosar.structura_motiv_gata_import" : "dosar.structura_motiv_gata");

  return (
    <div className="dossier">
      <div className="dossier-main">
        <Pasi pid={projectId} activ="dossier" />
        <BandaDoarCitire />
        <div className="page-head">
          {/* Numele proiectului E titlul acestei pagini, iar titlul se poate
              rescrie pe loc: dosarul e locul în care intră actele din care se
              află numele adevărat, deci și locul în care se corectează numele
              de lucru cu care s-a creat proiectul. În antet numele rămâne doar
              de citit. Lângă el, tipul: de el atârnă tot ce cere pagina. */}
          <TitluProiect id={projectId} />
          {tip && <EtichetaTip tip={tip} />}
          <Chip tone="mut">{tnr("dosar.fisiere#n", files.length)}</Chip>
          <div className="sp" />
          {/* Drumul spre soluția importată, ca BUTON, nu ca mesaj: apare când
              există unde duce și nu pretinde că s-a întâmplat ceva acum. */}
          {hasArchitecture && (
            <Link className="btn" to={`/p/${projectId}/solutii`}
                  title={t("dosar.vezi_solutiile_titlu")}>
              <Ic n="cube" s={14} />{t("dosar.vezi_solutiile")}
            </Link>
          )}
          {/* Actele urcate se citesc SINGURE, deci nu există un buton de
              «procesează»: aici doar se adaugă. Recitirea unui singur act
              rămâne pe rândul lui. */}
          <button className="btn" onClick={() => input.current?.click()}
                  disabled={busy || !potScrie}>
            <Ic n="plus" s={14} />
            {t(busy ? "comun.se_incarca" : "dosar.adauga_fisiere")}
          </button>
          {/* IEȘIREA DIN PAS: un singur buton primar, al tipului. La proiectul
              complet, spre Detalii teren — și fără acte, fiindcă lotul se poate
              desena și de mână. La cel de structură, pornirea structurii, care
              cere planșele și studiul. */}
          {/* Cât timp se citesc actele, butonul e BLOCAT: cine continua
              atunci ajungea la Detalii teren cu un lot pe jumătate citit și
              nu înțelegea de ce lipsesc date. Motivul stă pe hover, pe un
              înveliș — un buton `disabled` nu primește evenimente de mouse
              în toate browserele, deci `title`-ul lui nu s-ar vedea. */}
          {tip === "complet" && (
            <span className="blocat-tip"
                  title={t(running ? "dosar.continua_blocat"
                    : job?.status === "succeeded"
                      ? "dosar.continua_titlu_citit" : "dosar.continua_titlu")}>
              <button className="btn btnp" disabled={running}
                      onClick={() => nav(`/p/${projectId}/location`)}>
                {running && <span className="inel mic" aria-hidden="true" />}
                {t("dosar.continua")} <Ic n="arrow" s={14} />
              </button>
            </span>
          )}
          {structura && (
            <button className="btn btnp" onClick={genereazaStructura}
                    disabled={!structuraPoate || pornesteStructura}>
              {t(pornesteStructura ? "dosar.structura_porneste"
                 : areStructura ? "dosar.structura_deschide"
                 : importa ? "dosar.structura_importa" : "dosar.structura_genereaza")}
              <Ic n="arrow" s={14} />
            </button>
          )}
          <input ref={input} type="file" multiple hidden
                 onChange={(e) => upload(e.target.files)} />
        </div>
        {structura && motivStructura && (
          <p className={`dosar-motiv${structuraPoate ? " gata" : ""}`}>{motivStructura}</p>
        )}
        <PlanseSchimbate projectId={projectId} rand={arhGata} />

        {/* DOSARUL GOL, ca ecran în sine.
            Banda subțire de „trage aici" e mobilier bun lângă o listă de acte,
            dar pe un dosar în care nu e nimic ea e tot ce există pe ecran.
            Când nu e niciun act, aceeași zonă crește: spune cu ce se pornește
            (exemplele), ce se întâmplă cu ce lași aici, și că se poate și fără
            acte.

            Cât timp încărcarea n-a răspuns încă, `incarcat` ține ecranul gol:
            altfel starea goală ar clipi o dată pe fiecare deschidere de dosar
            plin. */}
        {tip === "complet" && potScrie && (golPustiu ? (
          <div className={`dropzone dropzone-gol ${dragging ? "over" : ""}`}
               role="button" tabIndex={0} title={t("dosar.adauga_fisiere")}
               onClick={() => input.current?.click()}
               onKeyDown={(e) => {
                 if (e.key !== "Enter" && e.key !== " ") return;
                 e.preventDefault();
                 input.current?.click();
               }}
               {...trasul}>
            <TeancDeActe />
            <b className="dz-titlu">{t("dosar.gol.titlu")}</b>
            <span className="dz-eticheta">{t("dosar.gol.exemple")}</span>
            <div className="dz-exemple">
              {EXEMPLE_GOL.map((cheie) => (
                <Chip key={cheie} tone="mut">{t(cheie)}</Chip>
              ))}
            </div>
            <span className="dz-sub">
              <b>{t("dosar.trage_aici")}</b>{t("dosar.trage_aici_rest")}
            </span>
          </div>
        ) : (
          <div className={`dropzone ${dragging ? "over" : ""}`} {...trasul}>
            <Ic n="doc" s={18} />
            <span>
              <b>{t("dosar.trage_aici")}</b>
              {t(running ? "dosar.trage_aici_citire" : "dosar.trage_aici_rest")}
            </span>
          </div>
        ))}

        {/* Ieșirea fără acte. Butonul «Continuă la localizare» de sus e
            același drum, dar el stă pe rândul de acțiuni al unui dosar plin;
            aici e răspunsul la întrebarea pe care o pune un ecran gol — «și
            dacă n-am actele?». O propoziție întreagă ca link, nu un cuvânt
            subliniat în mijlocul ei: ținta e mai mare decât cuvântul, iar fraza
            se citește la fel de bine cu voce tare. */}
        {tip === "complet" && potScrie && golPustiu && (
          <Link className="dz-sari" to={`/p/${projectId}/location`}>
            {t("dosar.gol.sari")} <Ic n="arrow" s={13} />
          </Link>
        )}


        {/* PROIECTUL DE STRUCTURĂ: cele două documente cerute, ca două locuri
            de umplut, iar dedesubt banda pentru tot restul. Se poate trage
            orice oriunde — clasificarea pune fiecare fișier în piesa lui. */}
        {structura && (
          <>
            <div className="locuri">
              <LocDeDosar fel="arh" fisiere={files.filter((f) => f.dosar === "solutie_arhitecturala")}
                          altulAre={areGeoteh} inLucru={inLucru} rand={randFisier}
                          laAlege={() => input.current?.click()} potScrie={potScrie}
                          peste={dragging} trasul={trasul} />
              <LocDeDosar fel="geo" fisiere={files.filter((f) => f.dosar === "studiu_geotehnic")}
                          altulAre={areArhDwg} inLucru={inLucru} rand={randFisier}
                          laAlege={() => input.current?.click()} potScrie={potScrie}
                          peste={dragging} trasul={trasul} />
            </div>
            {potScrie && (
              <div className={`dropzone ${dragging ? "over" : ""}`} {...trasul}>
                <Ic n="doc" s={18} />
                <span>
                  <b>{t("dosar.structura_trage")}</b>{t("dosar.structura_trage_rest")}
                </span>
              </div>
            )}
            <div className="dosar-optional">
              <span className="lbl">{t("dosar.structura_optional")}</span>
              <span className="chip c-out">{labels("retete_preturi")}</span>
              <span>{t("dosar.structura_retete")}</span>
            </div>
          </>
        )}

        {error && <p className="note bad">{error}</p>}

        {/* Cele două rulări ale dosarului. Amândouă TAC după ce reușesc — v.
            `PastilaJob`. */}
        <PastilaJob job={importJob} acum={acum}
                    titluRulare={t("dosar.import_ruleaza")}
                    titluEsec={t("dosar.import_esec")} />
        {job && running && !job.is_dead ? (
          <PanouCitire job={job} acum={acum} citite={acteCitite} total={files.length} />
        ) : (
          <PastilaJob job={job} acum={acum}
                      titluRulare={t("dosar.citire_ruleaza")}
                      titluEsec={t("dosar.citire_esec")} />
        )}

        <div className="groups">
          {/* ÎNCĂRCAREA ÎN CURS, ca un card ca toate celelalte, în capul
              listei: fișierele apar stinse, cu numele lor, iar bara de sus
              spune cât a plecat din tranșă. Sunt aici, nu într-o notă de sub
              butoane, fiindcă locul în care omul se uită după ce a dat drop e
              lista dosarului. */}
          {incarcari.map((incarcare) => (
            <div key={incarcare.id} className="card group group-incarcare">
              <div className="group-head">
                <Ic n="doc" s={16} />
                <b>{t("dosar.se_incarca_grup")}</b>
                <div className="sp" />
                <span className="lbl">
                  {incarcare.octeti > 0
                    ? t("dosar.urcat_din", { trimis: marime(incarcare.trimis),
                                             total: marime(incarcare.octeti) })
                    : t("dosar.se_trimite")}
                </span>
              </div>
              <div className="bara-progres"
                   role="progressbar"
                   aria-valuemin={0} aria-valuemax={100}
                   aria-valuenow={incarcare.octeti > 0
                     ? Math.round(100 * incarcare.trimis / incarcare.octeti)
                     : undefined}>
                {/* Fără total cunoscut (corp trimis în flux) bara nu are voie
                    să arate un procent: trece în nedeterminat. */}
                <span className={incarcare.octeti > 0 ? "" : "nedeterminat"}
                      style={incarcare.octeti > 0
                        ? { width: `${Math.min(100, Math.round(100 * incarcare.trimis / incarcare.octeti))}%` }
                        : undefined} />
              </div>
              {incarcare.nume.map((nume, i) => (
                <div key={`${nume}-${i}`} className="rowline rowline-stins">
                  <span className="fname">{nume}</span>
                  <Chip tone="mut">
                    <span className="pulsa" />{t("dosar.se_incarca_rand")}
                  </Chip>
                </div>
              ))}
            </div>
          ))}
          {grupuriVizibile.map(([key, group]) => (
            <div key={key} className="card group">
              <div className="group-head">
                <Ic n="doc" s={16} />
                <b>{labels(key)}</b>
                <div className="sp" />
                <span className="lbl">{tnr("dosar.fisiere#n", group.length)}</span>
              </div>
              {group.map((file) => randFisier(file, false, key))}
            </div>
          ))}
        </div>
      </div>

      {/* Panoul din dreapta e al FIȘIERULUI selectat și al nimic altceva. Cât
          timp nu e ales un fișier, nu apare: valorile citite din documente
          stăteau aici ca un afiș de date brute („necesita pud: da"), pe care
          nimeni nu-l citea și care fura un sfert de ecran de la dosar. */}
      {selected && (
        <aside className="card inspector">
          <div className="inspector-head">
            <b>{selected.name}</b>
            <a onClick={() => setSelected(null)}><Ic n="x" s={13} /></a>
          </div>
          <label className="field">
            <span className="lbl">{t("dosar.piesa")}</span>
            <select value={selected.dosar ?? "altele"} disabled={!potScrie}
                    onChange={(e) => recategorise(selected, e.target.value)}>
              {categories.map((c) => (
                <option key={c.cheie} value={c.cheie}>{c.eticheta}</option>
              ))}
            </select>
          </label>
          {/* și planșa PDF pe care pregătirea a parsat-o (vectorială, cu
              straturi): pentru ea `desene` are un rând */}
          {(/\.(dwg|dxf)$/i.test(selected.name) || !!desene[selected.id]) && (
            <>
            <Link className="btn" style={{ margin: "6px 0" }}
                  to={`/p/${projectId}/dossier/dwg/${selected.id}`}
                  title={t(desene[selected.id]?.stare === "gata"
                    ? "dosar.desen_gata_titlu" : "dosar.desen_lent_titlu")}>
              <Ic n="cube" s={14} />{t("dosar.deschide_viewer")}
            </Link>
            {/* CÂT VA DURA, înainte de clic. Un buton care uneori răspunde
                instantaneu și uneori după treizeci de secunde, fără să spună
                care dintre ele, e un buton în care omul nu mai are încredere. */}
            {desene[selected.id]?.stare === "gata" ? (
              <p className="note">
                {t("dosar.desen_gata")}
                {desene[selected.id]?.motiv
                  ? t("dosar.desen_gata_insa",
                      { motiv: desene[selected.id]?.motiv ?? "" }) : ""}
              </p>
            ) : desene[selected.id]?.stare === "eroare" ? (
              <p className="note bad">
                {t("dosar.desen_eroare",
                   { motiv: desene[selected.id]?.motiv ?? "" })}
              </p>
            ) : (
              <p className="note">{t("dosar.desen_in_fundal")}</p>
            )}
            </>
          )}
          <div className="pair">
            <span>{t("dosar.marime")}</span>
            <span className="mono">{marime(selected.size)}</span>
          </div>
          <div className="pair">
            <span>{t("dosar.folder")}</span>
            <span className="mono">{selected.folder || t("dosar.radacina")}</span>
          </div>
          {selected.categorie && (
            <div className="pair">
              <span>{t("dosar.plansa")}</span>
              <span className="mono">{selected.categorie}</span>
            </div>
          )}
          {/* FIȘIERUL CU MAI MULTE ACTE: ce conține, piesă cu piesă, cu
              paginile și cu ce s-a citit din fiecare. Nu se editează: scopul
              e să scoatem informația din dosar oricât de dezorganizat a fost
              urcat, nu să-l aranjăm. */}
          {selected.dosar === "mixt" && selected.piese?.length ? (
            <>
              <hr className="hr" />
              <span className="lbl">{t("dosar.piese")}</span>
              {selected.piese.map((p, i) => (
                <div key={i} className="pair">
                  <span>{labels(p.dosar)}</span>
                  <span className="mono">
                    {paginilePiesei(p.pagini)}
                    {p.stare && p.stare !== "citit" && p.dosar !== "altele"
                      ? ` · ${t("dosar.necitit")}` : ""}
                  </span>
                </div>
              ))}
            </>
          ) : null}
          {selected.dosar_sursa && (
            <>
              <hr className="hr" />
              <span className="lbl">{t("dosar.de_ce_aici")}</span>
              <p className="note">
                {DE_UNDE[selected.dosar_sursa]
                  ? t(DE_UNDE[selected.dosar_sursa]) : selected.dosar_sursa}
              </p>
            </>
          )}
          {/* OFERTA: documentul e același pe Planificare → Oferte; aici se
              vede ce a ieșit din el acolo, și drumul până acolo */}
          {(selected.oferta || (selected.dosar === "retete_preturi" && citibilCaOferta(selected.name))) && (
            <>
              <hr className="hr" />
              <span className="lbl">{t("dosar.oferta")}</span>
              <p className={`note ${selected.oferta?.stare === "eroare" ? "bad" : ""}`}>
                {textOferta(selected.oferta)}
              </p>
              {(() => {
                const cale = registrul?.registru
                  ? caleOferte(projectId, registrul.registru, selected.oferta?.solution_id) : null;
                return cale && (
                  <Link className="btn" style={{ margin: "2px 0 6px" }} to={cale}>
                    {t("dosar.oferta_deschide")}
                  </Link>
                );
              })()}
            </>
          )}
          {selected.citire_stare && selected.citire_stare !== "citit" && (
            <>
              <hr className="hr" />
              <span className="lbl">{t("dosar.ce_s_a_intamplat")}</span>
              <p className={`note ${selected.citire_stare === "eroare" ? "bad" : ""}`}>
                {selected.citire_motiv || t("dosar.fara_motiv")}
              </p>
            </>
          )}
          {potScrie && (
            <>
              <hr className="hr" />
              {/* Reprocesarea are DOUĂ locuri, și nu din nehotărâre: icoana de
                  pe rând e pentru cine știe ce vrea, butonul de aici e pentru
                  drumul real al cazului — omul vine din viewer, unde a mutat
                  zona de interes, iar fișierul e deja selectat în panoul
                  ăsta. */}
              <button className="btn" disabled={reprocesate.has(selected.id)}
                      onClick={() => reprocess(selected)}
                      title={t("dosar.reproceseaza_act_titlu")}>
                <Ic n="redo" s={14} />
                {t(reprocesate.has(selected.id)
                  ? "dosar.se_reproceseaza_act" : "dosar.reproceseaza_act")}
              </button>
              <button className="btn" onClick={() => remove(selected)}>
                {t("dosar.sterge_fisierul")}
              </button>
            </>
          )}
        </aside>
      )}
    </div>
  );
}
